import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useWindowSize from "../../../../common/hooks/useWindowSize";
import breakpoints from "../../../../common/scss/breakpoints.module.scss";
import { useTranslation } from "react-i18next";
import { curveCardinal } from "d3";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
  type CustomScaleDefinition,
  type LabelProps,
  type MouseHandlerDataParam,
  type TooltipContentProps,
  type XAxisTickContentProps,
  type YAxisTickContentProps,
} from "recharts";
import defaultContent from "../../../../content/downlineProfile";
import keyMetricsContent from "../../../../content/keyMetrics";
import { ErrorTile } from "../ErrorTile/ErrorTile";
import { LeafErrorMessage } from "../../../../common/leaf/LeafErrorMessage/LeafErrorMessage";
import { IconNoData } from "../../../../common/icons/IconNoData/IconNoData";
import NoDataValue from "../../../../common/components/NoDataValue/NoDataValue";
import TrendChartSkeleton from "./TrendChartSkeleton";
import { storedLocale, marketCode } from "../../../Authentication/util";
import { getNumberFormatter } from "../../../../common/util/format";
import { getFullMonth, getNarrowMonth, getPerformanceYearShort, getShortMonth } from "../../../../common/util/period";
import { PY_MONTHS, type KpiSeries } from "../types";
import colors from "../../../../common/scss/colors.module.scss";
import "./TrendChart.scss";
import { TFunction } from "i18next";

interface TrendChartProps {
  series?: KpiSeries | null;
  endPeriod?: number;
  isLoading: boolean;
  isError?: boolean;
  onRetry?: () => void;
  searchId?: string;
}

interface LineConfig {
  dataKey: string;
  performanceYear: number;
  color: string;
  dash: string;
  label: string;
  lastClosedPeriod: number | null;
}

type ChartRow = {
  month: number;
  label: string;
  [dataKey: string]: number | string | null;
};

const MAX_YEARS = 3;

// Matches recharts' own default tickCount (see implicitYAxis in its axis selectors) so a
// range-bounded axis renders the same number of ticks as the auto-generated one.
const RANGE_TICK_COUNT = 5;

const SERIES_CONFIG: { lineColor: string; lineStyle: string }[] = [
  { lineColor: colors.blue, lineStyle: "" },
  { lineColor: colors.darkGrayText, lineStyle: "6 4" },
  { lineColor: colors.neutralGray, lineStyle: "2 2" },
];

const fallbackConfig = SERIES_CONFIG[SERIES_CONFIG.length - 1];

const LINE_CURVE = curveCardinal.tension(0.9);

const SCALE_TIERS: { max: number; divisor: number; labelKey: keyof typeof defaultContent.trendChart.scale | null }[] = [
  { max: 999, divisor: 1, labelKey: null },
  { max: 999_999, divisor: 1e3, labelKey: "thousands" },
  { max: 999_999_999, divisor: 1e6, labelKey: "millions" },
  { max: 999_999_999_999, divisor: 1e9, labelKey: "billions" },
  { max: Infinity, divisor: 1e12, labelKey: "trillions" },
];

const periodForMonth = (month: number, performanceYear: number): number =>
  month >= 9 ? (performanceYear - 1) * 100 + month : performanceYear * 100 + month;

const MONTH_FORMATTERS = {
  short: getShortMonth,
  long: getFullMonth,
  narrow: getNarrowMonth,
} as const;

const monthLabel = (month: number, locale: string, format: keyof typeof MONTH_FORMATTERS): string =>
  MONTH_FORMATTERS[format](`2000${`${month}`.padStart(2, "0")}`, locale);

// The chosen Y-axis ticks (see rangeYAxisProps) are real tier values, but the gaps between them are
// rarely equal (a tier set isn't always arithmetic) — so plotting them on an ordinary linear scale puts
// them at uneven pixel positions. This scale instead treats the tick VALUES themselves as breakpoints
// and places them at perfectly even pixel positions (0%, 25%, 50%, ..., 100% of the axis for 5 ticks),
// by construction rather than by any numeric relationship between them. A data value that isn't itself
// one of the tick breakpoints (e.g. a tier that exists but wasn't chosen as a tick) is positioned by
// linear interpolation between whichever two breakpoints it falls between, so it still lands in the
// right bracket relative to the (now evenly spaced) ticks around it.
const createTierRankScale = (breakpoints: number[]): CustomScaleDefinition<number> => {
  const lastIndex = breakpoints.length - 1;
  let domain: readonly number[] = [breakpoints[0], breakpoints[lastIndex]];
  let range: readonly number[] = [0, 1];

  const rankOf = (value: number): number => {
    const exact = breakpoints.indexOf(value);
    if (exact !== -1) return exact;
    let lower = 0;
    while (lower < lastIndex && breakpoints[lower + 1] <= value) lower += 1;
    const upper = Math.min(lower + 1, lastIndex);
    if (breakpoints[upper] === breakpoints[lower]) return lower;
    return lower + (value - breakpoints[lower]) / (breakpoints[upper] - breakpoints[lower]);
  };

  const scale = (value: number): number => {
    if (lastIndex === 0) return range[0];
    return range[0] + (rankOf(value) / lastIndex) * (range[1] - range[0]);
  };

  // recharts (via rechartsScaleFactory -> d3ScaleToRechartsScale) treats a function-valued `scale`
  // prop as a d3-scale-like object and calls `.copy().domain(axisDomain).range(axisRange)` on it
  // before invoking it as a function to position each value — CustomScaleDefinition's typing expects
  // that same shape, hence attaching domain/range/copy directly onto the callable rather than a
  // separate object.
  return Object.assign(scale, {
    domain: (next?: readonly number[]) => {
      if (next === undefined) return domain;
      domain = next;
      return scale;
    },
    range: (next?: readonly number[]) => {
      if (next === undefined) return range;
      range = next;
      return scale;
    },
    copy: () => createTierRankScale(breakpoints),
  }) as unknown as CustomScaleDefinition<number>;
};

type DotPosition = { dataKey: string; month: number; cy: number };

/**
 * Of the dots at the hovered month, return the one whose vertical position is closest to the cursor.
 * Exported for unit testing the hover-emphasis selection independent of recharts' DOM geometry.
 */
export const findNearestDot = (
  dots: Iterable<DotPosition>,
  activeMonth: number,
  cursorY: number,
): { dataKey: string; month: number } | null => {
  let nearest: { dataKey: string; month: number } | null = null;
  let nearestDist = Infinity;
  for (const dot of dots) {
    if (dot.month !== activeMonth) continue;
    const dist = Math.abs(dot.cy - cursorY);
    if (dist < nearestDist) {
      nearestDist = dist;
      nearest = { dataKey: dot.dataKey, month: dot.month };
    }
  }
  return nearest;
};

export const NoKpiData = ({ t }: { t: TFunction<readonly ["downlineProfile", "keyMetrics"], undefined> }) => {
  return (
    <div className="TrendChart TrendChart--empty" data-testid="trend-chart-empty">
      <LeafErrorMessage
        show={true}
        icon={<IconNoData height="12.5rem" width="15.66rem" />}
        headline={t("trendChart.emptyMessage", defaultContent.trendChart.emptyMessage) as string}
        fontSize="big"
        gap={true}
        noContent={true}
      />
    </div>
  );
};

const TrendChart = ({ series, endPeriod, isLoading, isError, onRetry, searchId = "" }: TrendChartProps) => {
  const { t } = useTranslation(["downlineProfile", "keyMetrics"]);

  const locale = storedLocale();
  const market = marketCode();

  // The legend is a sibling of the chart and can't know the auto-sized y-axis gutter width, so we
  // measure the rendered x-axis start from the DOM and offset the legend to align it with the plot.
  const chartRef = useRef<HTMLDivElement>(null);
  const [legendOffset, setLegendOffset] = useState(0);

  const [hoveredDot, setHoveredDot] = useState<{ dataKey: string; month: number } | null>(null);
  const [activeLabel, setActiveLabel] = useState<string | null>(null);
  const dotPositionsRef = useRef<Map<string, DotPosition>>(new Map());

  // Clear synchronously during render so renderDot (which runs during recharts' render) populates
  // a fresh map rather than having it wiped by a post-commit useEffect.
  const prevSeriesRef = useRef(series);
  const prevEndPeriodRef = useRef(endPeriod);
  if (prevSeriesRef.current !== series || prevEndPeriodRef.current !== endPeriod) {
    dotPositionsRef.current.clear();
    prevSeriesRef.current = series;
    prevEndPeriodRef.current = endPeriod;
  }

  const { width } = useWindowSize();
  const isMobile = (width ?? window.innerWidth) <= Number(breakpoints.desktop);

  const { rows, lineConfigs, hasData } = useMemo(() => {
    // API returns years ascending; reverse to newest-first so index 0 is the current PY.
    const orderedYears = [...(series?.years ?? [])].reverse().slice(0, MAX_YEARS);

    const currentPY = orderedYears[0]?.performanceYear ?? null;

    const configs: LineConfig[] = orderedYears.map((year, index) => {
      const lastPoint = year.points.reduce<number | null>((latest, point) => {
        return latest === null || point.period > latest ? point.period : latest;
      }, null);

      const style = SERIES_CONFIG[index] ?? fallbackConfig;

      return {
        dataKey: `py${year.performanceYear}`,
        performanceYear: year.performanceYear,
        color: style.lineColor,
        dash: style.lineStyle,
        label: t("trendChart.legendPyLabel", defaultContent.trendChart.legendPyLabel, {
          year: getPerformanceYearShort(`${year.performanceYear}08`),
        }),
        lastClosedPeriod: lastPoint,
      };
    });

    const chartRows: ChartRow[] = PY_MONTHS.map((month) => {
      const row: ChartRow = { month, label: monthLabel(month, locale, "short") };

      orderedYears.forEach((year) => {
        const point = year.points.find((p) => p.month === month);
        const period = periodForMonth(month, year.performanceYear);
        const isCurrentPY = year.performanceYear === currentPY;
        const isFuture = isCurrentPY && endPeriod !== undefined && period > endPeriod;
        row[`py${year.performanceYear}`] = point && !isFuture ? point.value.value : null;
      });

      return row;
    });

    const anyData = chartRows.some((row) => configs.some((c) => row[c.dataKey] !== null));

    return { rows: chartRows, lineConfigs: configs, hasData: anyData };
  }, [series, locale, endPeriod, t]);

  const renderOrder = useMemo(() => [...lineConfigs].reverse(), [lineConfigs]);

  // One shared scale for the whole axis (never mixed per-tick) — recharts generates evenly-spaced
  // ticks within a single linear domain, so every tick already shares the same order of magnitude.
  const { scaleDivisor, scaleLabelKey } = useMemo(() => {
    const max = rows.reduce(
      (acc, row) =>
        lineConfigs.reduce((innerAcc, cfg) => {
          const v = row[cfg.dataKey];
          return typeof v === "number" ? Math.max(innerAcc, Math.abs(v)) : innerAcc;
        }, acc),
      0,
    );
    const tier = SCALE_TIERS.find((tier) => max <= tier.max) ?? SCALE_TIERS[SCALE_TIERS.length - 1];
    return { scaleDivisor: tier.divisor, scaleLabelKey: tier.labelKey };
  }, [rows, lineConfigs]);

  const handleMouseMove = useCallback(
    (next: MouseHandlerDataParam) => {
      const cursorY = next.activeCoordinate?.y;
      const rawIndex = next.activeTooltipIndex;
      const index = typeof rawIndex === "string" || typeof rawIndex === "number" ? Number(rawIndex) : undefined;
      const activeMonth = index !== undefined ? rows[index]?.month : undefined;
      if (cursorY === undefined || !next.isTooltipActive || activeMonth === undefined) {
        setHoveredDot(null);
        setActiveLabel(null);
        return;
      }
      setActiveLabel(rows[index as number]?.label ?? null);
      setHoveredDot(findNearestDot(dotPositionsRef.current.values(), activeMonth, cursorY));
    },
    [rows],
  );

  const handleMouseLeave = useCallback(() => {
    setHoveredDot(null);
    setActiveLabel(null);
  }, []);

  // Measure the x-axis start (= auto y-axis gutter) and apply it as the legend's left offset.
  // Re-measure on resize/data change; no-ops until the axis line is in the DOM.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const measure = () => {
      const axisLine = chart.querySelector(".recharts-yAxis .recharts-cartesian-axis-line");
      if (!axisLine) return;
      const offset = axisLine.getBoundingClientRect().left - chart.getBoundingClientRect().left;
      setLegendOffset(offset > 0 ? offset : 0);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(chart);
    return () => observer.disconnect();
    // chartRef.current is a DOM node; including chartRef (stable ref object) ensures the effect
    // re-runs if the element mounts after the initial render.
  }, [chartRef, rows, isMobile, hasData]);

  // Server-formatted values for the tooltip, keyed by `${dataKey}:${month}`.
  const formattedLookup = useMemo(() => {
    const map = new Map<string, string>();
    (series?.years ?? []).forEach((year) => {
      year.points.forEach((point) => {
        map.set(`py${year.performanceYear}:${point.month}`, point.value.formatted);
      });
    });
    return map;
  }, [series]);

  const metricName = series?.kpi ? t(series.kpi, keyMetricsContent[series.kpi] ?? series.kpi) : "";
  const chartAriaLabel = t("trendChart.chartAriaLabel", defaultContent.trendChart.chartAriaLabel, {
    metric: metricName,
  });

  const formatValue = useCallback(
    (value: number) =>
      getNumberFormatter(locale, market, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value),
    [locale, market],
  );

  // Y-axis ticks are abbreviated to a single shared scale (see scaleDivisor above); the tooltip and
  // screen-reader table keep showing full, unscaled values via formatValue. performanceBonus values
  // are already whole percents (e.g. 21 meaning 21%), so style: "percent" needs value/100 to render
  // the locale-correct "%" (position/spacing varies by locale, hence Intl over string concatenation).
  const formatYAxisValue = useCallback(
    (value: number) => {
      const scaled = value / scaleDivisor;
      if (series?.kpi === "performanceBonus") {
        return getNumberFormatter(locale, market, {
          style: "percent",
          minimumFractionDigits: 0,
          maximumFractionDigits: 1,
        }).format(scaled / 100);
      }
      return getNumberFormatter(locale, market, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      }).format(scaled);
    },
    [locale, market, scaleDivisor, series?.kpi],
  );

  // recharts measures ticks by their injected `recharts-cartesian-axis-tick-value` class to size the
  // `width="auto"` gutter, so that class MUST be preserved or the gutter collapses and labels clip.
  const renderYAxisTick = useCallback(
    ({ x, y, payload, className }: YAxisTickContentProps & { className?: string }) => (
      <text x={x} y={y} dy={4} textAnchor="end" className={`${className ?? ""} TrendChart__y-tick`.trim()}>
        {formatYAxisValue(payload.value as number)}
      </text>
    ),
    [formatYAxisValue],
  );

  // performanceBonus supplies a market-specific set of valid bonus tiers (e.g. ESAN [3,6,9,...,21],
  // ANA [3,6,9,...,25]) — every plotted value is itself always one of these tiers, so every tick must
  // be a real tier value (never interpolated/decimal) and the axis always shows exactly
  // RANGE_TICK_COUNT ticks starting at 0. The top is the smallest tier covering the actual plotted
  // data (extended if needed so there are enough tiers below it — see minTopIndex) rather than always
  // the range's global max, so a low-value ABO's chart isn't squeezed into a sliver at the bottom of a
  // 0-21%/0-25% axis. The remaining ticks target evenly-spaced fractions of that max and each snaps to
  // whichever real tier is nearest, scanning left-to-right so picks stay strictly increasing. Once those
  // RANGE_TICK_COUNT tick values are chosen, they — not the full tier list — become the scale's
  // breakpoints (see createTierRankScale): each chosen tick is placed at an exactly even pixel position
  // (0%, 25%, 50%, 75%, 100%) by construction, regardless of how far apart its numeric value is from its
  // neighbors. A plotted data point that lands on a tier between two ticks is interpolated between them.
  // Every other KPI keeps recharts' fully automatic ticks/domain/scale (props below are simply omitted,
  // not set to undefined, when no range is present).
  const rangeYAxisProps = useMemo(() => {
    const range = series?.range;
    if (!range || range.length === 0) return undefined;
    const sorted = [...range].sort((a, b) => a - b);

    const actualMax = rows.reduce(
      (acc, row) =>
        lineConfigs.reduce((innerAcc, cfg) => {
          const v = row[cfg.dataKey];
          return typeof v === "number" ? Math.max(innerAcc, v) : innerAcc;
        }, acc),
      0,
    );

    const coverIndex = sorted.findIndex((tier) => tier >= actualMax);
    const minTopIndex = Math.min(RANGE_TICK_COUNT - 2, sorted.length - 1);
    const topIndex = Math.max(coverIndex === -1 ? sorted.length - 1 : coverIndex, minTopIndex);
    const max = sorted[topIndex];
    const candidates = sorted.slice(0, topIndex + 1);

    const nonZeroTickCount = RANGE_TICK_COUNT - 1;
    let searchFrom = 0;
    const nonZeroTicks = Array.from({ length: nonZeroTickCount }, (_, i) => {
      if (i === nonZeroTickCount - 1) return max;
      const target = ((i + 1) * max) / nonZeroTickCount;
      const maxAllowedIndex = candidates.length - nonZeroTickCount + i;
      let bestIndex = searchFrom;
      for (let j = searchFrom; j <= maxAllowedIndex; j++) {
        if (Math.abs(candidates[j] - target) <= Math.abs(candidates[bestIndex] - target)) {
          bestIndex = j;
        }
      }
      searchFrom = bestIndex + 1;
      return candidates[bestIndex];
    });
    const ticks = Array.from(new Set([0, ...nonZeroTicks]));

    return { ticks, domain: [0, max] as [number, number], scale: createTierRankScale(ticks) };
  }, [series?.range, rows, lineConfigs]);

  // Anchors at the axis's top-left corner (recharts' viewBox) and rotates around that point, so the
  // label hugs the corner regardless of string length instead of relying on length-tuned dx/dy offsets.
  // dominantBaseline="hanging" keeps the glyph ascent below/right of the pivot pre-rotation, so after
  // the -90deg rotation it extends into the chart rather than past the left edge of the container.
  // Because the text is rotated -90deg, a local dx shift renders as a vertical shift after rotation;
  // this small constant corrects the "hanging" baseline's residual gap to the true glyph top and is a
  // fixed font-metric correction, not a per-string-length offset.
  const renderYAxisLabel = useCallback(
    ({ viewBox }: LabelProps) => {
      if (!viewBox || !scaleLabelKey) return null;
      const { x, y } = viewBox as { x: number; y: number };
      return (
        <text
          x={x}
          y={y}
          dx={4}
          transform={`rotate(-90, ${x}, ${y})`}
          textAnchor="end"
          dominantBaseline="hanging"
          className="TrendChart__y-axis-label"
        >
          {t(`trendChart.scale.${scaleLabelKey}`, defaultContent.trendChart.scale[scaleLabelKey])}
        </text>
      );
    },
    [t, scaleLabelKey],
  );

  const renderTooltip = useCallback(
    ({ active, payload, label }: TooltipContentProps<number, string>) => {
      if (!active || !payload?.length) return null;
      const month = (payload[0]?.payload as ChartRow | undefined)?.month;
      const tooltipMonth = month !== undefined ? monthLabel(month, locale, "long") : label;
      return (
        <div className="TrendChart__tooltip" data-testid="trend-chart-tooltip">
          <p className="TrendChart__tooltip-month">{tooltipMonth}</p>
          {lineConfigs.map((cfg) => {
            const value = payload.find((p) => p.dataKey === cfg.dataKey)?.value;
            const isCurrentPY = cfg === lineConfigs[0];
            const isFuture =
              isCurrentPY &&
              endPeriod !== undefined &&
              month !== undefined &&
              periodForMonth(month, cfg.performanceYear) > endPeriod;
            if ((value === undefined || value === null) && !isFuture) return null;
            const formatted =
              value === undefined || value === null
                ? null
                : (month !== undefined && formattedLookup.get(`${cfg.dataKey}:${month}`)) ||
                  formatValue(value as number);
            const isHovered = hoveredDot !== null && hoveredDot.dataKey === cfg.dataKey && hoveredDot.month === month;
            return (
              <div
                className={`TrendChart__tooltip-row${isHovered ? " TrendChart__tooltip-row--active" : ""}`}
                key={cfg.dataKey}
              >
                <span
                  className="TrendChart__tooltip-swatch"
                  style={{ backgroundColor: cfg.color }}
                  aria-hidden="true"
                />
                <span className="TrendChart__tooltip-label">{cfg.label}</span>
                <span className="TrendChart__tooltip-value">
                  {formatted ?? (
                    <NoDataValue
                      label={
                        t("trendChart.screenReader.noValue", defaultContent.trendChart.screenReader.noValue) as string
                      }
                    />
                  )}
                </span>
              </div>
            );
          })}
        </div>
      );
    },
    [lineConfigs, formattedLookup, formatValue, locale, hoveredDot, endPeriod, t],
  );

  // X-axis tick: emphasize the current PY's last closed month; use numeric labels on mobile.
  const renderXAxisTick = useCallback(
    (props: XAxisTickContentProps) => {
      const { x, y, index } = props;
      const month = rows[index]?.month;
      const currentConfig = lineConfigs[0];
      const isLastClosed =
        currentConfig !== undefined &&
        currentConfig.lastClosedPeriod !== null &&
        month !== undefined &&
        periodForMonth(month, currentConfig.performanceYear) === currentConfig.lastClosedPeriod;
      const tickLabel =
        month !== undefined ? (isMobile ? month.toString().padStart(2, "0") : monthLabel(month, locale, "short")) : "";

      return (
        <text
          x={x}
          y={y}
          dy={16}
          textAnchor="middle"
          className={`TrendChart__x-tick${isLastClosed ? " TrendChart__x-tick--current" : ""}`}
        >
          {tickLabel}
        </text>
      );
    },
    [rows, lineConfigs, isMobile, locale],
  );

  const renderDot = useCallback(
    (cfg: LineConfig) => (props: { cx?: number; cy?: number; payload?: ChartRow; value?: number }) => {
      const { cx, cy, payload, value } = props;
      if (cx === undefined || cy === undefined || value === undefined || value === null) {
        return <g key={`${cfg.dataKey}-empty`} />;
      }
      const isCurrentPY = cfg === lineConfigs[0];
      const month = payload?.month;
      // Record this dot's position so the mouse-move handler can find the line nearest the cursor.
      if (month !== undefined) {
        dotPositionsRef.current.set(`${cfg.dataKey}:${month}`, { dataKey: cfg.dataKey, month, cy });
      }
      const isLastClosed =
        isCurrentPY &&
        cfg.lastClosedPeriod !== null &&
        month !== undefined &&
        periodForMonth(month, cfg.performanceYear) === cfg.lastClosedPeriod;
      const isHovered = hoveredDot !== null && hoveredDot.dataKey === cfg.dataKey && hoveredDot.month === month;

      if (isLastClosed || isHovered) {
        return (
          <g key={`${cfg.dataKey}-${month}`}>
            <circle cx={cx} cy={cy} r={11} fill={cfg.color} fillOpacity={0.36} style={{ cursor: "pointer" }} />
            <circle
              cx={cx}
              cy={cy}
              r={6}
              fill={cfg.color}
              stroke={cfg.color}
              strokeWidth={2}
              style={{ cursor: "pointer" }}
            />
          </g>
        );
      }
      return (
        <circle key={`${cfg.dataKey}-${month}`} cx={cx} cy={cy} r={4} fill={cfg.color} style={{ cursor: "pointer" }} />
      );
    },
    [lineConfigs, hoveredDot],
  );

  if (isError) {
    return (
      <div className="TrendChart TrendChart--error" data-testid="trend-chart-error">
        <ErrorTile errorStatus={true} isLoading={false} clickFunction={() => onRetry?.()} tealiumTrackId="trendChart" />
      </div>
    );
  }

  if (isLoading) {
    return (
      <TrendChartSkeleton ariaLabel={t("trendChart.loadingAria", defaultContent.trendChart.loadingAria) as string} />
    );
  }

  if (!series || !hasData) {
    return <NoKpiData t={t} />;
  }

  return (
    <div className={`TrendChart ${searchId ? searchId : ""}`} data-testid="trend-chart">
      <div
        ref={chartRef}
        className="TrendChart__chart"
        role="img"
        aria-label={chartAriaLabel as string}
        data-testid="trend-chart-graph"
      >
        <ResponsiveContainer width="100%" height={250}>
          <LineChart
            data={rows}
            margin={{ top: 15, right: 0, bottom: 0, left: 0 }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <CartesianGrid vertical={false} stroke={colors.lightGray} />
            <XAxis
              dataKey="label"
              interval={0}
              tickLine={false}
              axisLine={false}
              tick={renderXAxisTick}
              padding={{ left: 16, right: 16 }}
            />
            <YAxis
              label={scaleLabelKey ? renderYAxisLabel : undefined}
              tickLine={false}
              axisLine={{ stroke: colors.lightGray, strokeWidth: 1, strokeDasharray: "2 2" }}
              width="auto"
              tickMargin={8}
              tick={renderYAxisTick}
              allowDecimals={false}
              {...rangeYAxisProps}
            />
            <Tooltip content={renderTooltip} cursor={false} animationDuration={0} />
            {renderOrder.map((cfg) => (
              <Line
                key={cfg.dataKey}
                type={LINE_CURVE}
                dataKey={cfg.dataKey}
                name={cfg.label}
                stroke={cfg.color}
                strokeWidth={2}
                strokeDasharray={cfg.dash || undefined}
                connectNulls={false}
                isAnimationActive={false}
                dot={renderDot(cfg)}
                activeDot={false}
                data-testid={`trend-chart-line-${cfg.dataKey}`}
              />
            ))}
            {activeLabel !== null && (
              <ReferenceLine
                x={activeLabel}
                stroke={colors.blue}
                strokeWidth={1}
                strokeDasharray={"2 2"}
                ifOverflow="extendDomain"
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <ul className="TrendChart__legend" style={{ marginLeft: legendOffset }} data-testid="trend-chart-legend">
        {renderOrder.map((cfg) => (
          <li
            className="TrendChart__legend-item"
            key={cfg.dataKey}
            data-testid={`trend-chart-legend-item-${cfg.dataKey}`}
          >
            <svg className="TrendChart__legend-swatch" width="24" height="8" aria-hidden="true">
              <line
                x1="0"
                y1="4"
                x2="24"
                y2="4"
                stroke={cfg.color}
                strokeWidth="4"
                strokeDasharray={cfg.dash || undefined}
              />
            </svg>
            <span className="TrendChart__legend-label">{cfg.label}</span>
          </li>
        ))}
      </ul>

      <table className="sr-only" data-testid="trend-chart-data-table">
        <caption>
          {t("trendChart.screenReader.dataTableCaption", defaultContent.trendChart.screenReader.dataTableCaption, {
            metric: metricName,
          })}
        </caption>
        <thead>
          <tr>
            <th scope="col">
              {t("trendChart.screenReader.monthColumnHeader", defaultContent.trendChart.screenReader.monthColumnHeader)}
            </th>
            {renderOrder.map((cfg) => (
              <th scope="col" key={cfg.dataKey}>
                {cfg.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.month}>
              <th scope="row">{monthLabel(row.month, locale, "long")}</th>
              {renderOrder.map((cfg) => {
                const value = row[cfg.dataKey];
                const formatted =
                  value === null || value === undefined
                    ? t("trendChart.screenReader.noValue", defaultContent.trendChart.screenReader.noValue)
                    : (formattedLookup.get(`${cfg.dataKey}:${row.month}`) ?? formatValue(value as number));
                return <td key={cfg.dataKey}>{formatted}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TrendChart;
